import { defineStore, acceptHMRUpdate } from "pinia";
import { brogglBack } from "../axios.js";
import { clientReportTypes } from "../assets/variables.js";
import router from "../router/index.js";
import dayjs from "dayjs";

export const useClientReportStore = defineStore("clientReport", {
  state: () => ({
    total: 0,
    chart: {},
    barChartKey: 0,
    empty: false,
    chartData: {
      labels: [],
      datasets: [],
    },
    parameters: {
      breakdown: "Month",
      period: "Last Quarter",
      clientReportType: clientReportTypes[0].value,
    },
    totals: {},
    details: {},
    clientDetailsIsLoaded: true,
    TimeSpentOnProjectPerClient: [],
    timeSpentOnProjectParameters: {
      period: "Last Quarter",
    }
  }),
  getters: {
    getTotal: (state) => state.total,
    getChart: (state) => state.chart,
    getParameters: (state) => state.parameters,
    getBarChartKey: (state) => state.barChartKey,
    getChartData: (state) => state.chartData,
    getEmpty: (state) => state.empty,
    getTotals: (state) => state.totals,
    getDetails: (state) => state.details,
    getClientDetailsIsLoaded: (state) => state.clientDetailsIsLoaded,
    getTimeSpentOnProjectPerClient: (state) => state.TimeSpentOnProjectPerClient,
    getTimeSpentOnProjectParameters: (state) => state.timeSpentOnProjectParameters
  },
  actions: {
    async getReport() {
      this.chart = {};
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.total = 0;
      this.empty = false;
      if (this.parameters.clientReportType === "project") {
        const res = await brogglBack.get("/report/client/project-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            breakdown: this.parameters.breakdown,
            clientId: router.currentRoute.value.params.id,
          },
        });
        let sumCS = [];
        let sumPS = [];
        let sumOnBoarding = [];
        let sumOtherProjects = [];
        for (let i = 0; i < res.data.data.length; i++) {
          this.total += res.data.data[i].sumAllProjects;
          sumCS.push(res.data.data[i].sumCS);
          sumPS.push(res.data.data[i].sumPS);
          sumOnBoarding.push(res.data.data[i].sumOnBoarding);
          sumOtherProjects.push(res.data.data[i].sumOtherProjects);
        }
        this.chartData.datasets.push({
          label: "CS",
          backgroundColor: "#217981",
          borderColor: "#000000",
          data: sumCS,
        });
        this.chartData.datasets.push({
          label: "PS",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          data: sumPS,
        });
        this.chartData.datasets.push({
          label: "OnBoarding",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          data: sumOnBoarding,
        });
        this.chartData.datasets.push({
          label: "Other Projects",
          backgroundColor: "#48d4d4",
          borderColor: "#000000",
          data: sumOtherProjects,
        });

        this.chartData.labels = res.data.data.map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data;
      } else if (this.parameters.clientReportType === "billable") {
        const res = await brogglBack.get("/report/client/billable-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            breakdown: this.parameters.breakdown,
            period: this.parameters.period,
            clientId: router.currentRoute.value.params.id,
          },
        });
        let sumBillable = [];
        let sumNonBillable = [];
        let sumInternal = [];
        for (let i = 0; i < res.data.data.length; i++) {
          this.total += res.data.data[i].sumAll;
          sumBillable.push(res.data.data[i].sumBillable);
          sumNonBillable.push(res.data.data[i].sumNonBillable);
          sumInternal.push(res.data.data[i].sumInternal);
        }
        this.chartData.datasets.push({
          label: "Billable",
          backgroundColor: "#217981",
          borderColor: "#000000",
          fill: false,
          data: sumBillable,
        });
        this.chartData.datasets.push({
          label: "Non Billable",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          fill: false,
          data: sumNonBillable,
        });
        this.chartData.datasets.push({
          label: "Internal",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          fill: false,
          data: sumInternal,
        });

        this.chartData.labels = res.data.data.map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data;
      } else {
        const res = await brogglBack.get("/report/client/product-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            breakdown: this.parameters.breakdown,
            period: this.parameters.period,
            clientId: router.currentRoute.value.params.id,
          },
        });
        var attributes = [];
        Object.keys(res.data.data).forEach((key) => {
          Object.keys(res.data.data[key]).forEach((key2) => {
            if (!attributes.includes(key2)) attributes.push(key2);
          });
        });
        var datasets = [];
        for (const product of attributes) {
          const newList = [];
          datasets.push({ name: product, items: newList });
        }
        Object.keys(res.data.data).forEach((key) => {
          for (const product of attributes) {
            if (Object.hasOwn(res.data.data[key], product)) {
              datasets
                .find((item) => item.name === product)
                .items.push(res.data.data[key][product].sum);
            }
          }
        });
        for (const product of datasets) {
          for (let i = 0; i < product.items.length; i++) {
            this.total += product.items[i];
          }
        }
        const colors = ["#217981", "#4ca2cd", "#4ddbb8", "#48d4d4"];
        var i = 0;
        for (const product of datasets) {
          this.chartData.datasets.push({
            label: product.name,
            backgroundColor: colors[i],
            borderColor: "#000000",
            fill: false,
            data: product.items,
          });
          i = (i + 1) % colors.length;
        }
        Object.keys(res.data.data).forEach((breakdown) => {
          this.chartData.labels.push(dayjs(breakdown).format("MMM-DD-YYYY"));
        });
        this.chart = res.data.data;
      }
      this.barChartKey++;
      this.empty = this.chartData.datasets.every(
        (dataset) => dataset.data.length === 0,
      );
      this.total = this.total.toLocaleString();
    },
    calculateTotals() {
      this.totals = {};
      if (this.parameters.clientReportType === "project") {
        this.totals.CS = this.chart.reduce((acc, curr) => acc + curr.sumCS, 0);
        this.totals.PS = this.chart.reduce((acc, curr) => acc + curr.sumPS, 0);
        this.totals.OnBoarding = this.chart.reduce(
          (acc, curr) => acc + curr.sumOnBoarding,
          0,
        );
        this.totals.OtherProjects = this.chart.reduce(
          (acc, curr) => acc + curr.sumOtherProjects,
          0,
        );
      } else if (this.parameters.clientReportType === "billable") {
        this.totals.Billable = this.chart.reduce(
          (acc, curr) => acc + curr.sumBillable,
          0,
        );
        this.totals.NonBillable = this.chart.reduce(
          (acc, curr) => acc + curr.sumNonBillable,
          0,
        );
        this.totals.Internal = this.chart.reduce(
          (acc, curr) => acc + curr.sumInternal,
          0,
        );
      } else {
        Object.keys(this.chart).forEach((breakdown) => {
          Object.keys(this.chart[breakdown]).forEach((product) => {
            if (!Object.hasOwn(this.totals, product)) {
              this.totals[product] = this.chart[breakdown][product].sum;
            } else {
              this.totals[product] += this.chart[breakdown][product].sum;
            }
          });
        });
      }
      Object.keys(this.totals).forEach((total) => {
        total = total.toLocaleString();
      });
    },
    convertArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      if (
        this.parameters.clientReportType === "project" ||
        this.parameters.clientReportType === "billable"
      ) {
        let keys = Object.keys(data[0]);
        result += keys.join(columnDelimiter) + lineDelimiter;
        data.forEach(function (item) {
          Object.keys(item).forEach((key) => {
            result += item[key] + columnDelimiter;
          });
          result += lineDelimiter;
        });
        return result;
      } else {
        result +=
          "Date" +
          columnDelimiter +
          "Id" +
          columnDelimiter +
          "sum" +
          lineDelimiter;
        Object.keys(data).forEach((breakdown) => {
          Object.keys(data[breakdown]).forEach((product) => {
            result +=
              breakdown +
              columnDelimiter +
              product +
              columnDelimiter +
              data[breakdown][product].sum +
              lineDelimiter;
          });
        });
        return result;
      }
    },
    csvExport(arrData) {
      var data, filename, link;
      var csv = this.convertArrayOfObjectsToCSV(arrData);
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
    csvDetailsExport(arrData, reportType) {
      var data, filename, link;
      switch (reportType) {
        case "project":
          var csv = this.convertDetailedProjectArrayOfObjectsToCSV({
            data: arrData,
          });
          break;
        case "billable":
          var csv = this.convertDetailedProjectArrayOfObjectsToCSV({
            data: arrData,
          });
          break;
        case "product":
          var csv = this.convertDetailedProductArrayOfObjectsToCSV({
            data: arrData,
          });
          break;
        case "allEntries":
          var csv = this.convertDetailedEntriesArrayOfObjectsToCSV({
            data: arrData,
          });
          break;
        default:
          break;
      }
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
    convertDetailedProjectArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      result +=
        "Entries Type" +
        columnDelimiter +
        "Id" +
        columnDelimiter +
        "Description" +
        columnDelimiter +
        "Date" +
        columnDelimiter +
        "Test Id" +
        columnDelimiter +
        "Jira Id" +
        columnDelimiter +
        "Owner" +
        columnDelimiter +
        "Duration" +
        columnDelimiter +
        "Man days" +
        columnDelimiter +
        "Client Name" +
        columnDelimiter +
        "Role" +
        columnDelimiter +
        "Task Name" +
        columnDelimiter +
        "Project Name" +
        lineDelimiter;
      Object.keys(data).forEach((key) => {
        if (data[key] !== null) {
          data[key].forEach((item) => {
            if (item !== null) {
              result += key + columnDelimiter;
              Object.keys(item).forEach((key2) => {
                if (key2 === "Description") {
                  var description = JSON.stringify(
                    item[key2].replace("\n", " "),
                  );
                  if (description.includes("#")) {
                    description = description.replace(/#/g, " ");
                  }
                  result += description + columnDelimiter;
                } else if (key2 === "TaskName") {
                  var taskName = JSON.stringify(item[key2].replace(",", " ,"));
                  if (taskName.includes("#")) {
                    taskName = taskName.replace(/#/g, " ");
                  }
                  result += taskName + columnDelimiter;
                } else {
                  result += item[key2] + columnDelimiter;
                }
              });
              result += lineDelimiter;
            }
          });
        }
      });
      return result;
    },
    convertDetailedProductArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      result +=
        "Product Id" +
        columnDelimiter +
        "Id" +
        columnDelimiter +
        "Description" +
        columnDelimiter +
        "Date" +
        columnDelimiter +
        "Test Id" +
        columnDelimiter +
        "Jira Id" +
        columnDelimiter +
        "Owner" +
        columnDelimiter +
        "Duration" +
        columnDelimiter +
        "Man days" +
        columnDelimiter +
        "Client Name" +
        columnDelimiter +
        "Role" +
        columnDelimiter +
        "Task Name" +
        columnDelimiter +
        "Project Name" +
        lineDelimiter;
      Object.keys(data).forEach((key) => {
        if (data[key] !== null) {
          data[key].forEach((item) => {
            result += key + columnDelimiter;
            Object.keys(item).forEach((key2) => {
              if (key2 === "Description") {
                var description = JSON.stringify(item[key2].replace("\n", " "));
                if (description.includes("#")) {
                  description = description.replace(/#/g, " ");
                }
                result += description + columnDelimiter;
              } else if (key2 === "TaskName") {
                var taskName = JSON.stringify(item[key2].replace(",", " ,"));
                if (taskName.includes("#")) {
                  taskName = taskName.replace(/#/g, " ");
                }
                result += taskName + columnDelimiter;
              } else {
                result += item[key2] + columnDelimiter;
              }
            });
            result += lineDelimiter;
          });
        }
      });
      return result;
    },
    convertDetailedEntriesArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data.entries;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      result +=
        "Id" +
        columnDelimiter +
        "Description" +
        columnDelimiter +
        "Date" +
        columnDelimiter +
        "Test Id" +
        columnDelimiter +
        "Jira Id" +
        columnDelimiter +
        "Owner" +
        columnDelimiter +
        "Duration" +
        columnDelimiter +
        "Man days" +
        columnDelimiter +
        "Client Name" +
        columnDelimiter +
        "Role" +
        columnDelimiter +
        "Country" +
        columnDelimiter +
        "Pod" +
        columnDelimiter +
        "Task Name" +
        columnDelimiter +
        "Project Name" +
        lineDelimiter;
      data.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (key === "Description") {
            var description = JSON.stringify(item[key].replace("\n", " "));
            if (description.includes("#")) {
              description = description.replace(/#/g, " ");
            }
            result += description + columnDelimiter;
          } else if (key === "TaskName") {
            var taskName = JSON.stringify(item[key].replace(",", " ,"));
            if (taskName.includes("#")) {
              taskName = taskName.replace(/#/g, " ");
            }
            result += taskName + columnDelimiter;
          } else {
            result += item[key] + columnDelimiter;
          }
        });
        result += lineDelimiter;
      });
      return result;
    },
    async fetchReportDetails() {
      this.clientDetailsIsLoaded = false;
      try {
        const res = await brogglBack.get("/report/client/details", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            clientId: router.currentRoute.value.params.id,
            period: this.parameters.period,
            reportType: this.parameters.clientReportType,
            breakdown: this.parameters.breakdown,
          },
        });
        this.details = res.data.data;
        this.csvDetailsExport(this.details, this.parameters.clientReportType);
        this.clientDetailsIsLoaded = true;
      } catch (error) {
        console.log(error);
      }
    },
    async fetchEntriesDetails() {
      this.clientDetailsIsLoaded = false;
      try {
        const res = await brogglBack.get("/entries", {
          headers: { Authorization: "Bearer " + window.$cookies.get("jwt") },
          params: {
            clientId: router.currentRoute.value.params.id,
          },
        });
        this.details = res.data.data;
        this.csvDetailsExport(this.details, "allEntries");
        this.clientDetailsIsLoaded = true;
      } catch (error) {
        console.log(error);
      }
    },
    async fetchTimeSpentOnProjectPerClient() {
      const res = await brogglBack.get("/clients/time-spent-by-project", {
        headers: { Authorization: "Bearer " + window.$cookies.get("jwt") },
        params: {
          period: this.timeSpentOnProjectParameters.period,
        }
      });
      console.log(res.data);
      // Pre-compute formatted values for both hours and man-days
      console.log(res.data.data);
      let data = res.data.data;
      this.TimeSpentOnProjectPerClient = data.map(item => ({
        ...item,
        // Pre-compute both formats to avoid runtime calculations
        formatted: {
          CsandPsTotal: { hours: formatHours(item.csandPsTotal), days: item.csandPsTotal },
          CsandPsAvg: { hours: formatHours(item.csandPsAvg), days: item.csandPsAvg },
          OnbardingTotal: { hours: formatHours(item.onbardingTotal), days: item.onbardingTotal },
          OnbardingAvg: { hours: formatHours(item.onbardingAvg), days: item.onbardingAvg },
          CsTotal: { hours: formatHours(item.csTotal), days: item.csTotal },
          CsAvg: { hours: formatHours(item.csAvg), days: item.csAvg },
          PsTotal: { hours: formatHours(item.psTotal), days: item.psTotal },
          PsAvg: { hours: formatHours(item.psAvg), days: item.psAvg },
          SupportTotal: { hours: formatHours(item.supportTotal), days: item.supportTotal },
          SupportAvg: { hours: formatHours(item.supportAvg), days: item.supportAvg },
          ARR: item.arr.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          AnalyzeStartDate: new Date(item.AnalyzeStartDate).toLocaleDateString("en-GB", { dateStyle: "short" }).replace(/\//g, "-"),
          AnalyzeEndDate: new Date(item.AnalyzeEndDate).toLocaleDateString("en-GB", { dateStyle: "short" }).replace(/\//g, "-")
        }
      }));
    }
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useClientReportStore, import.meta.hot),
  );
}


function formatHours(valueInManDays) {
  if (valueInManDays === null || valueInManDays === undefined) {
    return "0.0";
  }
  if (typeof valueInManDays === "string") {
    valueInManDays = parseFloat(valueInManDays);
  }
  if (isNaN(valueInManDays)) {
    return "0.0";
  }
  return (valueInManDays * 8).toFixed(1);
}